import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InfoCard from '../../components/cards/info-card';
import orderGateHook from '../../api/orderGateHook';
import Spinner from '../../components/spinner/spinner';
import { PaymentContext } from '../../contexts/payment-context-provider';
import MinDetailsHeader from '../../components/min-details-header/MinDetailsHeader';
import PaymentMethodsCard from '../../components/cards/payment-methods-card';
import InactiveOrderNotice from '../../components/inactive-order-notice/inactive-order-notice';
import useDeviceInfo from '../../helpers/deviceDetect';
import { setAnalyticsUser, trackEvent, onUserLogin } from '../../utils/analytics';
import EventNames from '../../utils/event-name-constants';

function BillDetails() {
  const [billDetail, setBillDetail] = useState({ createdAt: null });
  const [orderDetail, setOrderDetail] = useState({});
  const [orderStatus, setPaymentStatus] = useState({});
  const [billStatus, setOrderStatus] = useState('');
  const { billId, orgId } = useParams();
  const [billBreakdown, setBillBreakdown] = useState([]);
  const [data, loading, error] = orderGateHook.useGetPrePaymentSummary({ preInvoiceId: billId, orgId });
  useContext(PaymentContext);
  const [, , makePayment] = orderGateHook.useMakePayment();
  const [instalmentDetails, setInstalmentDetails] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // A payment start this bill was refused with something to say - the order is
  // already paid, a payment was just started from it - shown in place of Pay Now,
  // with the way back to the order, where a fresh bill can be raised.
  const [refusal, setRefusal] = useState(null);
  const navigate = useNavigate();
  const { device, isWebView } = useDeviceInfo();
  const { PAYMENT_GATEWAY_OPENED } = EventNames;

  async function redirectToPaymentLink() {
    setIsProcessing(true);
    const paymentProperties = {
      price: billDetail?.totalPayable,
      productName: orderDetail?.orderItems?.[0]?.productMeta?.name,
      productId: orderDetail?.orderItems?.[0]?.productMeta?.id,
      batchId: orderDetail?.orderItems?.[0]?.productMeta?.selectedBatches?.[0]?.batchId,
      batchName: orderDetail?.orderItems?.[0]?.productMeta?.selectedBatches?.[0]?.name,
      exam: orderDetail?.orderItems?.[0]?.productMeta?.course?.exam?.name,
      session: orderDetail?.orderItems?.[0]?.productMeta?.course?.session?.name,
      stream: orderDetail?.orderItems?.[0]?.productMeta?.course?.stream?.name,
      center: orderDetail?.orderItems?.[0]?.productMeta?.course?.centre?.name,
      mode: orderDetail?.orderItems?.[0]?.productMeta?.course?.type,
      paymentMode: billDetail?.instalmentReference ? 'instalment' : 'full',
      deviceType: device === 'desktop' ? 'web' : device,
      screenName: 'Pay Now',
    };
    if (isWebView && device === 'android') {
      window.Android.paymentGatewayOpen(JSON.stringify(paymentProperties));
    } else if (isWebView && device === 'ios') {
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.callbackHandler) {
        window.webkit.messageHandlers.callbackHandler.postMessage(paymentProperties);
      }
    } else {
      trackEvent(PAYMENT_GATEWAY_OPENED, paymentProperties);
    }
    const response = await makePayment({
      prePaymentInvoiceId: billId,
      deviceType: device,
    });

    if (response?.paymentLink) {
      window.location.href = response.paymentLink; // Redirect to the payment link
    } else {
      setIsProcessing(false);
      if (response?.errorMessage) {
        setRefusal(response.errorMessage);
      } else {
        console.error('Payment link not received.');
      }
    }
  }

  useEffect(() => {
    setBillDetail(data?.prePaymentInvoice);
    setOrderDetail(data?.order);
    setPaymentStatus(String(data?.order?.paymentStatus)
      .charAt(0)
      .toUpperCase() + String(data?.order?.paymentStatus).slice(1));
  }, [loading]);

  useEffect(() => {
    setOrderStatus(String(billDetail.invoiceStatus)
      .charAt(0)
      .toUpperCase() + String(billDetail.invoiceStatus).slice(1));
    setBillBreakdown([]);
    setBillBreakdown((breakdown) => [...breakdown, { label: 'Taxable amount', amount: billDetail?.taxableAmount }]);
    setBillBreakdown((breakdown) => [...breakdown, { label: 'CGST', amount: billDetail?.cgst }]);
    setBillBreakdown((breakdown) => [...breakdown, { label: 'SGST', amount: billDetail?.sgst }]);
    setBillBreakdown((breakdown) => [...breakdown, { label: 'IGST', amount: billDetail?.igst }]);
    if (billDetail?.paymentType === 'instalment') {
      // console.log("Instalment: ", billDetail?.meta, billDetail?.userInstalmentPlan);
      setInstalmentDetails({
        totalInstalments: billDetail?.meta?.userInstalmentPlan?.userInstalmentPlanProperties?.totalInstalments,
      });
      if (!billDetail.meta) {
        setInstalmentDetails({
          totalInstalments: billDetail?.userInstalmentPlan?.totalInstalments,
        });
      }
    }
  }, [billDetail]);

  useEffect(() => {
    if (!orderDetail?.userMeta?.email) return;
    setAnalyticsUser(orderDetail.userMeta);
    onUserLogin(orderDetail.userMeta);
  }, [orderDetail]);

  return (
    <div className="flex flex-col min-h-screen pt-8">
      {!loading && !error && (
        <>
          {/* eslint-disable-next-line max-len */}
          <MinDetailsHeader
            skuName={orderDetail?.orderItems?.[0]?.skuDetails?.name}
            orderMessage=""
            orderId={billDetail?.orderId}
            orderCreatedAt={orderDetail?.createdAt}
            showMessage={false}
            courseType={orderDetail?.orderItems?.[0]?.productMeta?.course?.type ?? orderDetail?.orderItems?.[0]?.productMeta?.comboElements?.[0]?.course?.type}
            batchDetail={orderDetail?.orderItems?.[0]?.productMeta?.course?.exam?.name ?? orderDetail?.orderItems?.[0]?.productMeta?.comboElements?.[0]?.course?.exam?.name}
            hasTestPapers={Boolean(orderDetail?.orderItems?.[0]?.productMeta?.testPapers)}
            brand={orderDetail?.orderItems?.[0]?.productMeta?.brand}
            promotionalEventName={orderDetail?.orderItems?.[0]?.productMeta?.promotionalEventName}
            transferMeta={orderDetail?.orderMeta?.transferMeta}
          />

          <div className="flex flex-grow items-center flex-col relative">
            <div className="flex flex-grow flex-col w-[90%] lg:w-4/5 ">
              <PaymentMethodsCard
                paymentMethod={billDetail?.instalmentReference ? 'instalment' : 'full'}
                instalmentDetails={instalmentDetails}
              />

              {Object.keys(orderDetail?.userMeta || {}).length > 0 && (
              <InfoCard blueColor={false} className="mb-8" title="User Details">
                <div className="flex flex-col gap-2 px-2 ">
                  {orderDetail?.userMeta?.name && (
                  <div className="flex gap-1 items-baseline">
                    <span
                      className="text-[#334155] font-semibold text-[14px] md:text-[18px]"
                    >
                      Name:
                    </span>
                    <span
                      className="text-[#334155] text-[14px] md:text-[18px]"
                    >
                      {orderDetail?.userMeta?.name}
                    </span>
                  </div>
                  )}

                  {orderDetail?.userMeta?.phone && (
                  <div className="flex gap-1 items-baseline">
                    <span
                      className="text-[#334155] font-semibold text-[14px] md:text-[18px]"
                    >
                      Phone:
                    </span>
                    <span
                      className=" text-[14px] md:text-[18px] text-gray-800"
                    >
                      {orderDetail?.userMeta?.phone}
                    </span>
                  </div>
                  )}

                  {orderDetail?.userMeta?.email && (
                  <div className="flex gap-1 items-baseline">
                    <span
                      className="text-[#334155] font-semibold text-[14px] md:text-[18px]"
                    >
                      Email:
                    </span>
                    <span
                      className=" text-[14px] md:text-[18px] text-gray-800"
                    >
                      {orderDetail?.userMeta?.email}
                    </span>
                  </div>
                  )}

                </div>

              </InfoCard>
              )}

              <InfoCard
                title="Payment Breakdown"
                className="mb-8"
              >
                <div className="flex flex-col gap-2">
                  {billBreakdown.map((item, index) => (
                    <div
                      key={`${index + 1} f`}
                      className="flex px-[9px] justify-between items-center"
                    >
                      <span
                        className="text-[14px] md:text-[18px] font-medium text-[#334155]"
                      >
                        {item.label}
                      </span>
                      <span className="font-normal text-[14px] md:text-[18px] text-[#334155]">
                        INR
                        {' '}
                        {Math.abs(item.amount).toLocaleString()}
                      </span>
                    </div>
                  ))}
                  <div
                    className="flex justify-between items-center px-[10px] py-[6px] rounded-[6px] font-semibold text-[#334155] bg-[#D7F0FF] flex "
                  >
                    <span className="text-[14px] md:text-[18px] font-semibold">Total Amount</span>
                    <span className="text-[14px] md:text-[18px] font-normal">
                      INR
                      {billDetail.totalPayable}
                    </span>
                  </div>
                </div>
              </InfoCard>
            </div>
            {refusal && (
            <div
              className="sticky bottom-0 z-10 mt-auto w-full flex flex-col items-center gap-3 py-5 px-[19px] bg-[#FFF8E1] border-t-[1px] border-t-[#FFC107]"
            >
              <p className="text-[#8D6E63] font-semibold text-[15px] text-center">
                {refusal}
              </p>
              <button
                type="button"
                onClick={() => navigate(`/${orgId}/orders/${orderDetail?.id || billDetail?.orderId}`)}
                className="w-full md:w-1/2 lg:w-1/3 py-4 font-bold text-[16px] text-white rounded-[6px] bg-[#045692]"
              >
                Go Back
              </button>
            </div>
            )}
            {(!refusal && billStatus !== 'Paid' && orderStatus !== 'Inactive') && (
            <div
              className="w-full flex items-center justify-between py-5 px-[19px] bg-[#DFF3FF] border-t-[1px] border-t-[#81C4F5]"
            >
              <div className="text-[#334155]">
                <div className="md:text-[20px] lg:text-[22px] text-[16px]">
                  <span className="font-semibold"> Amount: </span>
                  INR
                  {' '}
                  {billDetail?.totalPayable}
                </div>
              </div>
              <button
                type="button"
                disabled={isProcessing}
                onClick={redirectToPaymentLink}
                className={`w-1/2 py-4 md:px-[133px] px-[40px] font-bold text-[16px] text-white rounded-[6px] ${isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#045692]'}`}
              >
                {isProcessing ? 'Processing...' : 'Pay Now'}
              </button>
            </div>
            )}
            {orderStatus === 'Inactive' && (
            <InactiveOrderNotice orgId={orgId} supersededBy={orderDetail?.orderMeta?.supersededBy} />
            )}

          </div>
        </>
      )}
      {loading && !error && <Spinner fullPage />}
    </div>
  );
}

export default BillDetails;
